import { useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Mail, ShieldCheck, CheckCircle2, MailWarning } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      if (data?.rate_limited) {
        setRateLimited(true);
        toast.message("We've already sent one — check your spam folder.");
      } else {
        setRateLimited(false);
      }
      setSent(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-[#050B1F]" data-testid="forgot-page">
      {/* Left brand column (matches Login) */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 text-slate-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "radial-gradient(45% 40% at 20% 10%, rgba(34,211,238,0.16), transparent 65%), radial-gradient(35% 30% at 90% 90%, rgba(244,63,94,0.12), transparent 65%)",
        }} />
        <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{
          backgroundImage: "linear-gradient(rgba(255,255,255,.35) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.35) 1px, transparent 1px)",
          backgroundSize: "36px 36px",
        }} />
        <div className="relative flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-md bg-gradient-to-br from-rose-500 via-amber-500 to-emerald-500 p-[1.5px]">
            <div className="h-full w-full rounded-[5px] bg-[#050B1F] flex items-center justify-center">
              <div className="h-1.5 w-1.5 rounded-full bg-rose-400" />
            </div>
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold text-[19px] tracking-tight leading-none text-white">RiskEye</div>
            <div className="text-[9.5px] font-mono uppercase tracking-[0.22em] text-cyan-300 mt-0.5">by seQure</div>
          </div>
        </div>
        <div className="relative">
          <div className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-cyan-300 mb-3">Secure recovery</div>
          <h2 className="font-display font-bold text-4xl text-white leading-tight">Reset your password.<br/><span className="text-cyan-300">Keep your posture intact.</span></h2>
          <p className="text-slate-400 text-[14px] mt-4 max-w-md leading-relaxed">
            Reset links are single-use, expire after one hour and are hashed at rest. Your TOTP secret and recovery codes are not affected — you'll still need your authenticator on next sign-in.
          </p>
        </div>
        <div className="relative flex items-center gap-3 text-[10.5px] font-mono uppercase tracking-[0.22em] text-slate-500">
          <span className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-emerald-400" /> Encrypted · SOC-ready</span>
          <span>·</span>
          <span>TLS 1.3 · AES-256</span>
        </div>
      </div>

      {/* Right form column */}
      <div className="flex items-center justify-center bg-white p-8 sm:p-12">
        <div className="w-full max-w-md">
          <Link to="/login" className="inline-flex items-center gap-1.5 text-[12.5px] text-slate-500 hover:text-slate-900 mb-8 font-medium" data-testid="back-to-login">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to sign-in
          </Link>

          {!sent ? (
            <form onSubmit={submit} data-testid="forgot-form">
              <div className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.28em] font-mono text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-full px-2.5 py-1 mb-4">
                <Mail className="h-3.5 w-3.5" /> Password reset
              </div>
              <h1 className="font-display font-bold text-3xl text-slate-900 leading-tight">Forgot your password?</h1>
              <p className="text-slate-500 text-[13.5px] mt-2 leading-relaxed">
                Enter the email tied to your account. If it exists, we'll send a one-time reset link. The link expires in one hour.
              </p>

              <div className="mt-8 space-y-4">
                <div>
                  <Label htmlFor="email" className="text-[12px] font-mono uppercase tracking-wider text-slate-500 mb-1.5 block">Work email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="h-11"
                    data-testid="forgot-email"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={busy || !email}
                  className="w-full h-11 bg-[#0A1329] hover:bg-[#162347] text-white font-medium text-[14px] tracking-tight transition-colors"
                  data-testid="forgot-submit"
                >
                  {busy ? "Sending reset link…" : "Send reset link →"}
                </Button>

                <div className="pt-4 border-t border-slate-200/70 text-center text-[12px] text-slate-500">
                  Don't have an account? <a href="mailto:hello@riskeye.io" className="text-slate-900 font-medium hover:underline">Contact us</a>
                </div>
              </div>
            </form>
          ) : rateLimited ? (
            <div data-testid="forgot-rate-limited">
              <div className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.28em] font-mono text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 mb-4">
                <MailWarning className="h-3.5 w-3.5" /> Already sent
              </div>
              <h1 className="font-display font-bold text-3xl text-slate-900 leading-tight">We've already sent one.</h1>
              <p className="text-slate-500 text-[13.5px] mt-3 leading-relaxed">
                A reset link for <span className="font-mono font-semibold text-slate-800">{email}</span> has been sent multiple times in the last hour. <span className="font-semibold text-slate-800">Check your spam folder</span> — it usually shows up as "Reset your RiskEye password".
              </p>
              <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50/50 p-4 text-[12.5px] text-slate-700 leading-relaxed">
                <div className="font-mono uppercase tracking-widest text-[10px] text-amber-700 mb-2">If you still can't find it</div>
                <ul className="space-y-1.5">
                  <li>· Search your inbox for <em>“RiskEye”</em> or <em>“Reset your RiskEye password”</em>.</li>
                  <li>· Check <em>Spam</em>, <em>Promotions</em>, and <em>All Mail</em>.</li>
                  <li>· Wait ~1 hour before requesting another link.</li>
                </ul>
              </div>
              <div className="mt-6 flex flex-wrap gap-3 items-center text-[12.5px]">
                <button
                  onClick={() => { setSent(false); setRateLimited(false); setEmail(""); }}
                  className="text-slate-500 hover:text-slate-900 font-medium"
                  data-testid="forgot-retry-rate"
                >
                  Try a different email
                </button>
                <span className="text-slate-300">·</span>
                <a href="mailto:hello@riskeye.io" className="text-slate-500 hover:text-slate-900 font-medium">Contact support</a>
              </div>
            </div>
          ) : (
            <div data-testid="forgot-sent">
              <div className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.28em] font-mono text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1 mb-4">
                <CheckCircle2 className="h-3.5 w-3.5" /> Link sent
              </div>
              <h1 className="font-display font-bold text-3xl text-slate-900 leading-tight">Check your inbox.</h1>
              <p className="text-slate-500 text-[13.5px] mt-3 leading-relaxed">
                If an account exists for <span className="font-mono font-semibold text-slate-800">{email}</span>, a reset link is on its way. The link expires in <span className="font-semibold text-slate-800">one hour</span>.
              </p>
              <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-4 text-[12.5px] text-slate-600 leading-relaxed">
                <div className="font-mono uppercase tracking-widest text-[10px] text-slate-400 mb-2">What next</div>
                <ul className="space-y-1.5">
                  <li>· Open the email and click <em>Reset password</em>.</li>
                  <li>· Set a password that meets our policy (12+ chars, mixed classes).</li>
                  <li>· Sign in — you'll still need your TOTP code.</li>
                </ul>
              </div>
              <div className="mt-6 flex flex-wrap gap-3 items-center text-[12.5px]">
                <button
                  onClick={() => { setSent(false); setEmail(""); }}
                  className="text-slate-500 hover:text-slate-900 font-medium"
                  data-testid="forgot-retry"
                >
                  Send to a different email
                </button>
                <span className="text-slate-300">·</span>
                <a href="mailto:hello@riskeye.io" className="text-slate-500 hover:text-slate-900 font-medium">Still stuck? Contact support</a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
